import { useState } from 'react';
import { Link2, Loader2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCombinedPaymentsApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';
import type { CombinedPaymentDetail } from '@ip/shared-types';

interface CombinedPaymentCardProps {
  companyNip: string;
  /** The invoice currently open in the detail view (highlighted in the list). */
  invoiceId: string;
  /** Purchase: undo only removes the link. Sale: undo also deletes the
      payments this confirmation registered — the confirm copy differs. */
  direction: 'purchase' | 'sale';
  payment: CombinedPaymentDetail;
  /** admin/ksiegowy — may undo the link (a financial correction). */
  canManage: boolean;
  /** Called after the link was undone — refresh the invoice. */
  onChanged: () => void;
}

/**
 * Płatność łączona on the invoice detail: which bank/card transaction paid
 * this invoice and which sibling invoices were covered by the same charge.
 * Undoing the link only removes this documentation — statuses stay as-is.
 */
export function CombinedPaymentCard({
  companyNip,
  invoiceId,
  direction,
  payment,
  canManage,
  onChanged,
}: CombinedPaymentCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { deleteCombinedPayment } = useCombinedPaymentsApi();

  const handleUnlink = async () => {
    try {
      setDeleting(true);
      await deleteCombinedPayment(companyNip, payment.id);
      toast.success(t('invoices.combinedPayment.unlinkSuccess'));
      setConfirmOpen(false);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('invoices.combinedPayment.unlinkError'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="flex items-center justify-between text-sm font-medium">
          <span className="flex items-center gap-1.5">
            <Link2 className="size-4 text-sky-600 dark:text-sky-400" />
            {t('invoices.combinedPayment.cardTitle')}
          </span>
          {canManage && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(true)}>
              <Unlink className="size-3.5" />
              {t('invoices.combinedPayment.unlink')}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 px-4">
        <p className="text-sm">
          {t('invoices.combinedPayment.cardTransaction', {
            date: payment.transactionDate,
            amount: `${formatPLN(payment.amount)} ${payment.currency}`,
          })}
          {payment.note ? <span className="text-muted-foreground"> — {payment.note}</span> : null}
        </p>
        <table className="w-full text-sm">
          <tbody>
            {payment.invoices.map((invoice) => (
              <tr
                key={invoice.id}
                className={
                  invoice.id === invoiceId
                    ? 'border-border/50 border-b font-medium last:border-0'
                    : 'border-border/50 border-b last:border-0'
                }
              >
                <td className="py-1 pr-2 font-mono text-xs">{invoice.invoiceNumber ?? '—'}</td>
                <td className="text-muted-foreground max-w-40 truncate py-1 pr-2">
                  {invoice.vendorName ?? '—'}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {invoice.gross != null ? formatPLN(invoice.gross) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-muted-foreground text-xs">
          {t('invoices.combinedPayment.cardCreatedBy', { name: payment.createdByName })}
        </p>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.combinedPayment.unlinkConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {direction === 'sale'
                ? t('invoices.combinedPayment.unlinkConfirmMessageSales', { count: payment.invoices.length })
                : t('invoices.combinedPayment.unlinkConfirmMessage', { count: payment.invoices.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                handleUnlink();
              }}
            >
              {deleting ? <Loader2 className="size-4 animate-spin" /> : null}
              {t('invoices.combinedPayment.unlink')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
